import React, { ReactElement, useEffect } from 'react';

import { NavigateFn, WindowLocation } from '@reach/router';
import {
  NavigateOptions,
  useLocation,
  useMatch as useMatchReactRouter,
  useNavigate,
  useParams,
} from 'react-router';
import { Route, Routes } from 'react-router-dom';

/**
 * @deprecated Quinyx react router v6 backfill for reach/router useMatch hook.
 * Do not use this, replace with react-router's useMatch
 * @param {string} pathname The path to match
 * @returns {object} Reach router compatible match object
 */
export const useMatch = (
  pathname: string,
): null | { uri: string; path: string; [param: string]: string } => {
  const match = useMatchReactRouter(pathname);
  if (!match) {
    return null;
  }
  return { uri: match.pathname, path: pathname, ...match.params };
};

/**
 * Quinyx react router v6 backfill for reach/router <Match/> component
 * https://reactrouter.com/en/main/upgrading/reach#match
 * Leaving any in place since this is the recommended backfill function from react-router themselves.
 * @returns {React.JSX.Element} The rendered ReactNode
 */
export const Match = ({
  path,
  children,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  path: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
}): React.JSX.Element => {
  const match = useMatch(path);
  const location = useLocation();
  const navigate = useNavigate();
  return children({ match, location, navigate });
};

interface IRedirect {
  default?: boolean;
  from?: string | null;
  to: string;
  noThrow?: boolean;
}

/**
 * Quinyx react router v6 backfill for reach/router <Redirect/> component
 * @returns {null} Returns null
 */
export const Redirect = ({
  from = null,
  to,
  noThrow = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  default: _default,
}: IRedirect): null => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    try {
      navigate(to);
    } catch (e) {
      if (!noThrow) {
        throw new Error(`Error Redirecting to "${to}"`);
      }
    }
  }, [from, location.pathname, navigate, to, noThrow]);

  return null;
};

interface TReachRouter {
  from?: string;
  to?: string;
  path: string;
  default?: boolean;
  children: ReactElement;
}

/**
 * @deprecated Component wrapper to backfill reach/router route props
 * E.g. location, path, navigate
 * @returns {React.ReactNode} The wrapped ReactNode
 */
function RouteElementWrapper({
  from,
  to,
  path,
  children,
}: TReachRouter): ReactElement {
  // const match = useMatch(path);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  if (!location.state) {
    location.state = {
      key: String(Date.now()),
    }; // at least key set is the default in reach router I think
  } else if (location.state && !location.state.key) {
    location.state.key = String(Date.now());
  }

  return React.cloneElement(children, {
    ...params,
    ...{ from, to, path, location, navigate },
  });
}

type TRouterWrapper = {
  children: React.ReactNode;
  primary?: boolean;
  className?: string;
};

// eslint-disable-next-line valid-jsdoc
/**
 * Route component wrapper to pass down reach/router style props to children
 * E.g. location, path, navigate
 * @returns {React.ReactElement<typeof Route> | null} The wrapped ReactElement or null
 */
const RenderRoute = (
  child: React.ReactNode,
): React.ReactElement<typeof Route> | null => {
  if (!React.isValidElement(child)) {
    return null;
  }

  let path;
  let from;
  let to;
  if (child && child.type && child.type === Redirect) {
    path = child.props?.from;
    from = child.props?.from;
    to = child.props?.to;
  } else {
    path = child.props.default ? '*' : child.props.path; // react router seems to strip leading slash whereas reach/router didnt
  }
  return (
    <Route
      path={path}
      element={
        <RouteElementWrapper path={path} from={from} to={to}>
          {child}
        </RouteElementWrapper>
      }
    />
  );
};

/**
 * Quinyx wrapper to make reach/router style JSX work with react router v6
 * @returns {React.ReactElement | null} (react router v6 component)
 */
const RouterWrapperReact = ({
  className,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  primary = true,
  children,
}: TRouterWrapper): React.ReactElement | null => {
  return (
    <div
      className={className}
      style={{
        outline: 'none',
      }}>
      <Routes>
        {React.Children.map(children, (child: React.ReactNode) => {
          if (React.isValidElement(child)) {
            if (child.type === React.Fragment) {
              return React.Children.map(
                child.props.children,
                (child2: React.ReactNode) => {
                  return RenderRoute(child2);
                },
              );
            }

            return RenderRoute(child);
          }
        })}
      </Routes>
    </div>
  );
};

export { RouterWrapperReact as Router };

/**
 * @deprecated globalHistory is not available in react-router v6
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const globalHistory = false;

/**
 * @deprecated
 * Quinyx backfill
 * Reach router backfill. Reach router <Redirect/> component would "throw" when activated.
 * However, since react router no longer does that, this can always return false.
 * @returns {boolean} false
 */
export const isRedirect = (): boolean => false;

export { Link } from 'react-router-dom';

export {
  HistoryLocationProvider as LocationProvider,
  createHistory,
  createMemorySource,
} from './HistoryLocationProvider';

// eslint-disable-next-line @typescript-eslint/ban-types
type ExtendedNavigateOptions<O = {}> = NavigateOptions & O;

export { ExtendedNavigateOptions as NavigateOptions };

export {
  useParams,
  // Router
  // Routes,
  // Route,
  useLocation,
  useNavigate,
  // Redirect,
  // RouteComponentProps,
  // useMatch,
  // Match,
  // isRedirect,
  // type NavigateOptions,
  // NavigateFn,
  // WindowLocation
  Location as WindowLocation,
  NavigateFunction as NavigateFn,
  NavigateFunction,
  MemoryRouter,
  BrowserRouter,
  // NavigateOptions,
} from 'react-router-dom';

/**
 * Quinyx backfill
 * This is a backfill for the RouteComponentProps from reach/router
 * It is not available in react-router v6
 * All props have to be optional because otherwise components start
 * to complain about missing props.
 */
export type RouteComponentProps<TParams = object> = Partial<TParams> & {
  path?: string | undefined;
  default?: boolean | undefined;
  location?: WindowLocation | undefined;
  navigate?: NavigateFn | undefined;
  uri?: string | undefined;
  // match?: {
  //   params: TParams;
  //   url: string;
  //   path: string;
  //   isExact: boolean;
  // };
  // history?: {
  //   push: ReturnType<typeof useNavigate>;
  //   // Add other history methods if needed
  // };
};
